import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  MenuItem,
  Box,
  Avatar,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';
import ClearIcon from '@mui/icons-material/Clear';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AddMemberDialog from './AddMemberDialog';
import { Person } from '../../../types/tree';
import { transformDate } from '../../../utils/date';
import { useGetFamilyStories, useUpdateMember } from '../../../hooks/treeHooks';
import { useGetMigrationRecordsByTreeId } from '../../../hooks';
import queryClient from '../../../core/http/react-query';
import { stringAvatar } from '../../../utils/transformTree';
import {
  capitalise,
  canDeleteMember,
  getCharacterCountHelperText,
  getLinkedNarrativesForMember,
  hasReachedCharacterLimit,
  sanitiseNameInput,
  sanitiseTextInput,
  truncateText,
} from '../../../utils';
import { useAuth } from '../../../components/auth/AuthProvider';
import {
  maxFamilyMembers,
  textLimits,
} from '../../../core/constants/constants';
import { MemberType } from '../../../types/tree';
import AvatarUploader from './AvatarUploader';
import useIsAdmin from '../../../hooks/useIsAdmin';
import useSubscription from '../../../hooks/useSubscription';
import { useModal } from '../../../components';
import SubscribeModal from '../../../components/common/SubscribeModal';

type EditFamilyMemberProps = {
  defaultValues: Person;
  closeDrawer: () => void;
  onAddMember: (data: any) => void | Promise<any>;
  treeMembers?: ReadonlyArray<any>;
  setRootId?: React.Dispatch<React.SetStateAction<string | null>>;
  openDeleteMemberModal: (name: string, nodeId: string) => void;
  firstRootId: string;
  treeId: string;
  resetRootHandler: () => void;
  rootId: string;
  collaborators: ReadonlyArray<any>;
  onOpenNarrative?: (type: 'story' | 'journey', id: string) => void;
};

export default function EditFamilyMember({
  defaultValues,
  closeDrawer,
  onAddMember,
  treeMembers,
  setRootId,
  openDeleteMemberModal,
  firstRootId,
  treeId,
  resetRootHandler,
  rootId,
  collaborators,
  onOpenNarrative,
}: EditFamilyMemberProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { openModal, closeModal } = useModal();
  const { control, handleSubmit, reset, watch } = useForm<Person>({
    defaultValues,
  });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState<boolean>(false);
  const [familyType, setFamilyType] = useState<string>('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);

  const isAlive = watch('is_alive', true);

  const open = Boolean(anchorEl);

  const isFamilyMemberLimitReached = treeMembers
    ? treeMembers?.length >= maxFamilyMembers
    : false;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isFamilyMemberLimitReached && !isPaidUser) {
      return openModal(
        <SubscribeModal
          closeModal={closeModal}
          handleClose={closeModal}
          message={`You can only add up to ${maxFamilyMembers} family members.`}
          title="Add More Family Members"
        />,
      );
    }

    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [updatedData, setUpdatedData] = useState<any>(defaultValues);

  const { currentUser } = useAuth();
  const { isAdmin } = useIsAdmin(currentUser);
  const { isPremium } = useSubscription();
  const userEmail = currentUser?.email;
  const isCollaborator = userEmail ? collaborators.includes(userEmail) : false;
  const isPaidUser = isPremium || isAdmin || isCollaborator;

  const { data: familyStoriesResponse } = useGetFamilyStories(treeId);
  const { data: migrationRecordsResponse } =
    useGetMigrationRecordsByTreeId(treeId);
  const familyStories = Array.isArray(familyStoriesResponse)
    ? familyStoriesResponse
    : (familyStoriesResponse?.data ?? []);
  const migrationRecords = Array.isArray(migrationRecordsResponse)
    ? migrationRecordsResponse
    : (migrationRecordsResponse?.data ?? []);
  const linkedNarratives = getLinkedNarrativesForMember(
    defaultValues?.id,
    familyStories,
    migrationRecords,
  );

  useEffect(() => {
    if (updatedData) {
      reset(updatedData);
    }
  }, [updatedData, reset]);

  const updateMutation = useUpdateMember(defaultValues?.id);

  const handleUpdate = (data: any) => {
    if (!currentUser) {
      return;
    }
    const formData = new FormData();
    formData.append('first_name', sanitiseNameInput(data.first_name));
    formData.append('middle_name', sanitiseNameInput(data.middle_name || ''));
    formData.append('last_name', sanitiseNameInput(data.last_name || ''));
    formData.append(
      'last_name_at_birth',
      sanitiseNameInput(data.last_name_at_birth || ''),
    );
    formData.append('date_of_birth', data.date_of_birth || '');
    formData.append('gender', data.gender);
    formData.append('is_alive', data.is_alive);
    formData.append('birth_place', sanitiseTextInput(data.birth_place || ''));
    formData.append('bio', sanitiseTextInput(data.bio || ''));
    formData.append('death_date', data.death_date || '');
    formData.append('updated_by', currentUser.uid);

    if (data?.photo_url instanceof File) {
      formData.append('photo_file', data.photo_url);
    }

    updateMutation.mutate(formData, {
      onSuccess: (updatedPerson) => {
        enqueueSnackbar('Family member details updated successfully!', {
          variant: 'success',
        });
        setIsEditing(false);
        setUpdatedData(updatedPerson);
        queryClient.refetchQueries({
          queryKey: ['familyTrees', treeId],
          exact: true,
        });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to update family member details', {
          variant: 'error',
        });
        console.error('Error updating member:', error);
      },
    });
  };

  const handleAddMember = async (data: any) => {
    setLoading(true);
    try {
      await Promise.resolve(onAddMember(data));
      if (!data?._keepOpen) {
        setIsAddMemberOpen(false);
        closeDrawer();
      }
    } catch (error) {
      console.error('Error adding member:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddMember = (type: string) => {
    setFamilyType(type);
    setIsAddMemberOpen(true);
  };

  const MemberName =
    `${updatedData.first_name} ${updatedData.last_name} ${updatedData.last_name_at_birth ? `(née ${updatedData.last_name_at_birth})` : ''}` as const;

  const { canDelete, message } = canDeleteMember(
    updatedData.id,
    treeMembers,
    firstRootId,
  );
  return (
    <Box>
      {isEditing ? (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4" py={1}>
              Edit details
            </Typography>
            <Tooltip title="Close">
              <IconButton onClick={() => setIsEditing(false)}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <form onSubmit={handleSubmit(handleUpdate)}>
            <Controller
              name="photo_url"
              control={control}
              render={({ field }) => (
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <AvatarUploader
                    src={field.value}
                    onFileUpload={(file: File) => {
                      field.onChange(file);
                    }}
                  />
                </div>
              )}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                overflowY: 'scroll',
                minHeight: 650,
                pt: { xs: 1 },
              }}
            >
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: textLimits.personName }}
                  />
                )}
              />
              <Controller
                name="middle_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Middle Name"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: textLimits.personName }}
                  />
                )}
              />
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: textLimits.personName }}
                  />
                )}
              />
              <Controller
                name="last_name_at_birth"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name at Birth"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: textLimits.personName }}
                  />
                )}
              />
              <Controller
                name="date_of_birth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    minDate={dayjs('1850-01-01').toDate()}
                    maxDate={dayjs().toDate()}
                    label="Date of Birth"
                    format="dd/MM/yyyy"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(
                        date instanceof Date && !isNaN(date.valueOf())
                          ? date.toISOString()
                          : null,
                      )
                    }
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <FormLabel>Gender</FormLabel>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        value="male"
                        control={<Radio />}
                        label="Male"
                      />
                      <FormControlLabel
                        value="female"
                        control={<Radio />}
                        label="Female"
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
              <FormControlLabel
                control={
                  <Controller
                    name="is_alive"
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field.value} />
                    )}
                  />
                }
                label="Is Alive?"
                sx={{ mt: 1 }}
              />
              {!isAlive && (
                <Controller
                  name="death_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      minDate={dayjs('1850-01-01').toDate()}
                      maxDate={dayjs().toDate()}
                      label="Date of Death"
                      format="dd/MM/yyyy"
                      value={field.value ? new Date(field.value) : null}
                      onChange={(date) =>
                        field.onChange(
                          date instanceof Date && !isNaN(date.valueOf())
                            ? date.toISOString()
                            : null,
                        )
                      }
                      slots={{ textField: TextField }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              )}
              <Controller
                name="birth_place"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Place of Birth"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{ mt: 2 }}
                    inputProps={{ maxLength: textLimits.birthPlace }}
                  />
                )}
              />
              <Controller
                name="bio"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Biography"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    multiline
                    rows={6}
                    sx={{ mt: 2 }}
                    inputProps={{ maxLength: textLimits.biography }}
                    error={hasReachedCharacterLimit(
                      field.value,
                      textLimits.biography,
                    )}
                    helperText={getCharacterCountHelperText(
                      field.value,
                      textLimits.biography,
                    )}
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                mt: 2,
                gap: 3,
                justifyContent: 'flex-end',
              }}
            >
              <Button variant="outlined" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
            </Box>
          </form>
        </>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="600">
              {MemberName}
            </Typography>
            <Tooltip title="Close">
              <IconButton onClick={closeDrawer}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            mb={2}
          >
            <Avatar
              {...stringAvatar(updatedData)}
              src={updatedData?.photo_url}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              py: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.2,
              }}
            >
              <IconButton onClick={() => setIsEditing(true)}>
                <EditNoteIcon color="primary" />
              </IconButton>
              <Typography variant="caption">Edit details</Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.2,
                }}
              >
                <IconButton onClick={handleClick} color="primary">
                  <GroupAddIcon />
                </IconButton>
                <Typography variant="caption">Add a member</Typography>
              </Box>

              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                {!updatedData?.father_id && (
                  <MenuItem
                    onClick={() => handleOpenAddMember(MemberType.FATHER)}
                  >
                    Father
                  </MenuItem>
                )}
                {!updatedData?.mother_id && (
                  <MenuItem
                    onClick={() => handleOpenAddMember(MemberType.MOTHER)}
                  >
                    Mother
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => handleOpenAddMember(MemberType.SPOUSE)}
                >
                  Spouse/ Ex
                </MenuItem>
                <MenuItem
                  onClick={() => handleOpenAddMember(MemberType.SIBLING)}
                >
                  Sibling
                </MenuItem>
                <MenuItem onClick={() => handleOpenAddMember(MemberType.CHILD)}>
                  Child
                </MenuItem>
              </Menu>
            </Box>
            {canDelete ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.2,
                }}
              >
                <IconButton
                  onClick={() => {
                    openDeleteMemberModal(MemberName, updatedData.id);
                    closeDrawer();
                  }}
                  color="primary"
                >
                  <PersonRemoveIcon />
                </IconButton>
                <Typography variant="caption">Delete member</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.2,
                }}
              >
                <Tooltip title={message} arrow>
                  <span>
                    <IconButton disabled color="primary">
                      <PersonRemoveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="caption">Delete member</Typography>
              </Box>
            )}
          </Box>
          <Divider />
          {rootId !== firstRootId || rootId !== defaultValues?.id ? (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                display: 'flex',
                justifyContent: 'space-around',
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.action.hover,
              }}
            >
              {rootId !== defaultValues?.id && defaultValues?.id ? (
                <Tooltip title="Make this person the top of the tree" arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.2,
                    }}
                  >
                    <IconButton
                      color="primary"
                      aria-label="Show tree from here"
                      onClick={() => {
                        if (setRootId) setRootId(defaultValues.id);
                        closeDrawer();
                      }}
                    >
                      <AccountTreeIcon />
                    </IconButton>
                    <Typography variant="caption">Family view</Typography>
                  </Box>
                </Tooltip>
              ) : null}
              {rootId !== firstRootId ? (
                <Tooltip title="Go back to the original root person" arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.2,
                    }}
                  >
                    <IconButton
                      color="warning"
                      aria-label="Reset to root user"
                      onClick={() => {
                        if (resetRootHandler) resetRootHandler();
                        closeDrawer();
                      }}
                    >
                      <RestartAltIcon />
                    </IconButton>
                    <Typography variant="caption">Reset to root</Typography>
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ overflowY: 'scroll', maxHeight: 650 }}>
            <Typography variant="h5" py={1} fontWeight="500">
              Information
            </Typography>
            <Divider />
            <Box py={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Name
              </Typography>
              <Typography>{MemberName}</Typography>
            </Box>
            {updatedData?.middle_name && (
              <Box py={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Middle name
                </Typography>
                <Typography>{updatedData?.middle_name}</Typography>
              </Box>
            )}
            {updatedData?.last_name_at_birth && (
              <Box display={'flex'} gap={2} alignItems={'center'}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Last Name at Birth
                </Typography>
                <Typography>{updatedData?.last_name_at_birth}</Typography>
              </Box>
            )}
            {updatedData?.gender && (
              <Box py={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Gender
                </Typography>
                <Typography>{capitalise(updatedData?.gender)}</Typography>
              </Box>
            )}
            <Divider />
            {updatedData?.date_of_birth && (
              <Box py={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Born
                </Typography>
                <Typography>
                  {transformDate(updatedData?.date_of_birth)}
                </Typography>
              </Box>
            )}
            {updatedData?.birth_place && (
              <Box py={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Birth place
                </Typography>
                <Typography>{updatedData.birth_place}</Typography>
              </Box>
            )}
            {!updatedData?.is_alive ? (
              <Box py={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Living status
                </Typography>
                <Typography>Deceased</Typography>
                <Typography>Death date</Typography>
                <Typography>
                  {transformDate(updatedData?.death_date)}
                </Typography>
              </Box>
            ) : (
              <Box py={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Living status
                </Typography>
                <Typography>Living</Typography>
              </Box>
            )}
            <Divider />

            <Box display={'flex'} flexDirection={'column'} gap={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Biography
              </Typography>
              <Typography variant="body2" lineHeight={1.8}>
                {updatedData?.bio || 'No bio available.'}
              </Typography>
            </Box>
            <Box py={2}>
              <Divider sx={{ mt: 2 }} />
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Linked stories & journeys
              </Typography>
              {linkedNarratives.length ? (
                <Box>
                  {linkedNarratives.map((narrative) => (
                    <Box
                      key={`${narrative.type}-${narrative.id}`}
                      onClick={() =>
                        onOpenNarrative?.(narrative.type, narrative.id)
                      }
                      sx={{
                        cursor: onOpenNarrative ? 'pointer' : 'default',
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                        alignItems: 'center',
                        mb: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        color="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenNarrative?.(narrative.type, narrative.id);
                        }}
                      >
                        <RateReviewOutlinedIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                        }}
                      >
                        {truncateText(narrative.title)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 999,
                          backgroundColor:
                            narrative.type === 'story'
                              ? 'primary.light'
                              : 'secondary.light',
                          color:
                            narrative.type === 'story'
                              ? 'primary.main'
                              : 'secondary.main',
                        }}
                      >
                        {narrative.type}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2">
                  No stories or journeys linked to this person yet.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <AddMemberDialog
        isAddMemberOpen={isAddMemberOpen}
        setIsAddMemberOpen={setIsAddMemberOpen}
        familyType={familyType}
        handleAddMember={handleAddMember}
        selectedPerson={defaultValues}
        treeMembers={treeMembers}
        loading={loading}
        memberName={MemberName}
      />
    </Box>
  );
}
